import React, { useEffect, useState } from "react";
import {
	Outlet,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";

import { Translate } from "@/app/application/Translate";
import { UserContext } from "@/app/providers";
import { MaterialHandlingBreadcrumbs } from "@/features/material-handling/ui";
import { ShowUpdatedProcedures } from "@/features/procedures/pages/updated-procedures-page";
import { ProcedureUpdates } from "@/features/task";
import { sharedSecondaryTabsSx, sharedTabsSx } from "@/styles/sharedNavStyles";
// import components
import { Badge, Box, Tab, Tabs, styled } from "@mui/material";

export const MaterialHandlingPage = () => {
	const navigateTo = useNavigate();
	const [selectedMainTab, setSelectedMainTab] = useState(0);
	const [selectedSecondaryTab, setSelectedSecondaryTab] = useState(0);
	const location = useLocation();

	const user = React.useContext(UserContext);

	const isProcedureSuggestionsAdministrator =
		user?.IsProcedureSuggestionsAdministrator;

	// Routing defined here
	useEffect(() => {
		// Set the main and secondary tabs based on the current path
		const path = location.pathname;

		// Logic for setting the main tab
		if (path.includes("/materialhandling/updates/")) {
			setSelectedMainTab(1); // Procedure Updates tab
			if (isProcedureSuggestionsAdministrator) {
				// Administrator logic
				if (path.includes("/newImprov")) setSelectedSecondaryTab(0);
				else if (path.includes("/myImprov")) setSelectedSecondaryTab(1);
				else if (path.includes("/verified")) setSelectedSecondaryTab(2);
				else if (path.includes("/declined")) setSelectedSecondaryTab(3);
			} else {
				// Non-administrator logic
				if (path.includes("/myImprov")) setSelectedSecondaryTab(0);
				else if (path.includes("/verified")) setSelectedSecondaryTab(1);
				else if (path.includes("/declined")) setSelectedSecondaryTab(2);
			}
		} else if (
			path.includes("/materialhandling/approval/pending") ||
			path.includes("/materialhandling/approval/approve/")
		) {
			setSelectedMainTab(2); // Pending Approval tab
		} else {
			setSelectedMainTab(0); // Default to Task tab
		}
	}, [location.pathname, isProcedureSuggestionsAdministrator]);

	const StyledBadge = styled(Badge)(({ theme, color }) => ({
		"& .MuiBadge-badge": {
			border: `1px solid ${theme.palette[color]?.main ?? theme.palette.divider}`,
			padding: "0 4px",
			color: theme.palette.text.primary,
			backgroundColor: "transparent",
		},
	}));

	const StyledTab = styled((props) => (
		<Tab
			{...props}
			label={
				<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
					{props.label}
					{props.badge && props.color && (
						<StyledBadge
							variant="standard"
							badgeContent={props.badge ? props.badge : ""}
							color={props.color}
							sx={{ "& .MuiBadge-badge": { marginTop: "-2px" } }}
						/>
					)}
				</div>
			}
		/>
	))({});

	const handleMainTabChange = (event, newValue) => {
		setSelectedMainTab(newValue);
		setSelectedSecondaryTab(0); // Reset secondary tab when changing main tab
		switch (newValue) {
			case 0:
				navigateTo("/materialhandling/");
				break;
			case 1:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/materialhandling/updates/newImprov");
				} else {
					navigateTo("/materialhandling/updates/myImprov");
				}
				break;
			case 2:
				navigateTo("/materialhandling/approval/pending");
				break;
			default:
				break;
		}
	};

	const handleSecondaryTabChangeProcedure = (event, newValue) => {
		setSelectedSecondaryTab(newValue);
		switch (newValue) {
			case 0:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/materialhandling/updates/newImprov");
				} else {
					navigateTo("/materialhandling/updates/myImprov");
				}
				break;
			case 1:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/materialhandling/updates/myImprov");
				} else {
					navigateTo("/materialhandling/updates/verified");
				}
				break;
			case 2:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/materialhandling/updates/verified");
				} else {
					navigateTo("/materialhandling/updates/declined");
				}
				break;
			case 3:
				navigateTo("/materialhandling/updates/declined");
				break;
			default:
				break;
		}
	};
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: 1,
				minHeight: 0,
				overflow: "hidden",
				bgcolor: "background.default",
			}}
		>
			<Tabs
				value={selectedMainTab}
				onChange={handleMainTabChange}
				textColor="inherit"
				indicatorColor="primary"
				variant="scrollable"
				scrollButtons="auto"
				allowScrollButtonsMobile
				sx={{ flexShrink: 0, ...sharedTabsSx }}
			>
				<StyledTab
					label={
						<Translate String="MaterialHandling.MaterialHandling">
							Material Handling
						</Translate>
					}
				/>
				<StyledTab
					label={
						<Translate String="MaterialHandling.MaterialHandlingImprovements">
							Material Handling Improvements
						</Translate>
					}
				/>
				<StyledTab
					label={
						<Translate String="MaterialHandling.PendingApproval">
							Pending Approval
						</Translate>
					}
				/>
			</Tabs>

			{selectedMainTab === 0 && <></>}
			{selectedMainTab === 1 && (
				<Tabs
					value={selectedSecondaryTab}
					onChange={handleSecondaryTabChangeProcedure}
					textColor="inherit"
					indicatorColor="primary"
					variant="scrollable"
					scrollButtons="auto"
					allowScrollButtonsMobile
					sx={{ flexShrink: 0, ...sharedSecondaryTabsSx }}
				>
					{isProcedureSuggestionsAdministrator && (
						<StyledTab
							label={<Translate String="Task.Improvement.New.Improvements" />}
						/>
					)}
					<StyledTab
						label={<Translate String="Task.Improvement.My.Improvements" />}
						color="error"
					/>
					<StyledTab
						label={
							<Translate String="Task.Improvement.Verified.Improvements" />
						}
						color="success"
					/>
					<StyledTab
						label={
							<Translate String="Task.Improvement.Declined.Improvements" />
						}
					/>
				</Tabs>
			)}

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					bgcolor: "background.default",
				}}
			>
				<Box
					sx={{
						flexShrink: 0,
						bgcolor: "background.default",
						py: 0.75,
						px: { xs: 1.5, sm: 2 },
					}}
				>
					<MaterialHandlingBreadcrumbs />
				</Box>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Routes>
						<Route
							path="/materialhandling/approval/pending"
							component={ShowUpdatedProcedures}
						/>
						<Route
							path="/materialhandling/updates/newImprov"
							render={() => <ProcedureUpdates page="newImprov" />}
						/>
						<Route
							path="/materialhandling/updates/myImprov"
							render={() => <ProcedureUpdates page="myImprov" />}
						/>
						<Route
							path="/materialhandling/updates/verified"
							render={() => <ProcedureUpdates page="verified" />}
						/>
						<Route
							path="/materialhandling/updates/declined"
							render={() => <ProcedureUpdates page="declined" />}
						/>
						{/* <Route path="/materialhandling/edit/:id" component={EditTask} /> */}
					</Routes>
					<Outlet />
				</Box>
			</Box>
		</Box>
	);
};

export default MaterialHandlingPage;
